import type { CampaignStatus } from '~/types/graphql';
import type { IUsedTemplateSuffixes } from '../types';
import type { IThemePage } from '~/stores/themePage';

import {
  PICK_PAGE_TYPE_BY_VARIANT_URL_RESOURCE_TYPE,
  groupBaseIDsByPickPageType,
  useVariantUrlRedirectLivePages,
} from '~/modules/campaign-url-redirect';
import { sortByConditions, toRenderedHomeTemplateSuffix } from '~/utils';
import { hasVariantUrlDestination } from '~/modules/campaign/helpers';
import { CAMPAIGN_STATUS, CAMPAIGN_TYPE } from '~/constants/campaign';
import { useCampaignStore } from '~/modules/campaign/stores';
import { IPickPageType } from '~/modules/modal-pick-page';
import { ICampaignType } from '~/modules/campaign/types';
import { useMemo } from 'react';

interface IUrlVariantUsage {
  /** The destination record the variant points at. Empty for the homepage, which has no record. */
  baseID: string;
  type: IPickPageType;
  urlId: string;
  destinationName?: string;
  campaignId: string;
  campaignName?: string;
  campaignStatus?: CampaignStatus;
}

export interface IUrlCampaignTemplateUsageProps {
  excludedCampaignId?: string;
  /**
   * Every template the theme holds a file for, by asset key. Read here rather than off the assets:
   * the caller shares one pass with the other usage hooks. Undefined until the theme has loaded,
   * which resolves no template at all — a destination cannot be told to render the default template
   * before then, and reading it as one would lock that template by mistake.
   */
  templateAssetKeys?: Set<string>;
  /**
   * The theme's pages, to resolve the home template serving `/` — a destination pointing at the
   * homepage has no record to read one off. Read by the caller for the same reason as the keys above.
   */
  themePages?: IThemePage[];
}

export interface IUrlCampaignTemplateUsageResult {
  urlCampaignTemplateUsage: IUsedTemplateSuffixes;
  handleRefetchUrlDestinations: () => Promise<unknown>;
  isFetchingUrlDestinations: boolean;
}

/**
 * A URL test locks the page it sends traffic to, not a template: its variants carry a destination
 * record and no `templateSuffix`. Which template that page renders only lives on the record behind
 * it, so those records have to be looked up before the picker can tell that template is taken.
 */
export const useUrlCampaignTemplateUsage = ({
  excludedCampaignId,
  templateAssetKeys,
  themePages,
}: IUrlCampaignTemplateUsageProps): IUrlCampaignTemplateUsageResult => {
  const dataCampaigns = useCampaignStore((state) => state.campaigns);

  const urlVariantUsages = useMemo<IUrlVariantUsage[]>(() => {
    const urlCampaigns = (dataCampaigns ?? []).filter(
      (item) =>
        item.type === CAMPAIGN_TYPE.URL &&
        item.status !== CAMPAIGN_STATUS.STOPPED &&
        !item.deletedAt &&
        item.id !== excludedCampaignId,
    );

    return urlCampaigns.flatMap((campaign) =>
      // Control side first: an experiment is collapsed to its first destination, and only removing
      // the Control's record hands the whole pair back — a Variant with nothing to be measured
      // against is not a test.
      sortByConditions(campaign.variants ?? [], [{ attr: 'isOrigin' }]).flatMap((variant) => {
        const url = variant?.url;
        if (!hasVariantUrlDestination(url) || !url) return [];

        // Both sides of a test can point at pages rendering the same template, so an experiment
        // raises a usage per side and is collapsed by campaign further down.
        const type = PICK_PAGE_TYPE_BY_VARIANT_URL_RESOURCE_TYPE[url.resourceType];
        // Every other page type is looked up by its record, so one with no id resolves to no
        // template. Only the homepage legitimately has none — the theme answers for it instead.
        if (!url.baseID && type !== IPickPageType.HOME) return [];

        return [
          {
            baseID: url.baseID,
            type,
            urlId: url.id,
            destinationName: url.title ?? undefined,
            campaignId: campaign.id,
            campaignName: campaign.name,
            campaignStatus: campaign.status ?? undefined,
          },
        ];
      }),
    );
  }, [dataCampaigns, excludedCampaignId]);

  // HOME falls in no group: it has no record to look up, so the theme resolves its template instead.
  const baseIDGroups = useMemo(
    () => groupBaseIDsByPickPageType(urlVariantUsages),
    [urlVariantUsages],
  );

  const { livePageByBaseID, handleRefetch, isFetching } = useVariantUrlRedirectLivePages({
    ...baseIDGroups,
    templateAssetKeys,
  });

  const homeTemplateSuffix = useMemo(
    () => toRenderedHomeTemplateSuffix({ themePages, templateAssetKeys }),
    [themePages, templateAssetKeys],
  );

  const urlCampaignTemplateUsage = useMemo<IUsedTemplateSuffixes>(() => {
    const usage: IUsedTemplateSuffixes = {};

    urlVariantUsages.forEach(
      ({ baseID, type, urlId, destinationName, campaignId, campaignName, campaignStatus }) => {
        const templateSuffix =
          type === IPickPageType.HOME
            ? homeTemplateSuffix
            : livePageByBaseID[baseID]?.templateSuffix;
        if (!templateSuffix) return;

        (usage[templateSuffix] ??= []).push({
          campaignId,
          campaignName,
          campaignStatus,
          campaignType: ICampaignType.URL,
          isOtherUrlCampaign: true,
          urlId,
          resourceName: destinationName,
        });
      },
    );

    return usage;
  }, [urlVariantUsages, livePageByBaseID, homeTemplateSuffix]);

  return {
    urlCampaignTemplateUsage,
    handleRefetchUrlDestinations: handleRefetch,
    isFetchingUrlDestinations: isFetching,
  };
};
